import React, { useEffect, useState } from "react";
import moment from "moment";
import strings from "translate/language";
import StatusBadge, { CheckInStatusBadge } from "component/common/StatusBadge";
import {
  formatAppointmentCellValue,
  getClosingStatusBadgeClass,
  resolveCheckInDisplay,
  resolveClosingStatusLabel,
} from "utils/appointmentListStatusDisplay";
import appointmentService from "reduxStore/service/appointmentService";
import "./AppointmentGroupHistoryDrawer.css";
import "../../screens/Appointment/AppointmentListing.css";

const formatSiteVisit = (item) => {
  if (!item?.appointment_date) return "-";
  return (
    moment(item.appointment_date).utc().format("DD-MM-yyyy") +
    " & " +
    (item.appointment_time || "")
  );
};

const formatHistoryDateTime = (date) => {
  if (!date) return "N/A";
  const d = moment(date);
  if (!d.isValid()) return "N/A";
  return d.format("DD/MM/YYYY · h:mm A");
};

const isCmHistoryRemark = (text) => {
  const p = String(text || "").trim();
  return (
    p.startsWith("(CM)") ||
    /^re-allocated visitor to new closing manager/i.test(p) ||
    /^re-allocated from\b/i.test(p) ||
    /^re-allocated to\b/i.test(p) ||
    /^assign appointment to new cm/i.test(p) ||
    p.startsWith("[CM Assignment]")
  );
};

const renderHistoryRemarkText = (text) =>
  isCmHistoryRemark(text) ? <strong>{text}</strong> : text;

const getVisitState = (item) => {
  const checkInDisplay = resolveCheckInDisplay(item);
  return {
    showAsVisited: checkInDisplay.showAsVisited,
    checkInLabel: checkInDisplay.label,
    closingLabel: resolveClosingStatusLabel(item),
  };
};

const ViewAction = ({ onView, itemId }) =>
  onView ? (
    <button
      type="button"
      className="appointment-listing__action-btn appt-history-drawer__view-btn"
      title="View Appointment"
      onClick={() => onView(itemId)}
    >
      <i className="fa fa-eye" aria-hidden="true" />
    </button>
  ) : null;

const CurrentVisitSummary = ({ item, onView }) => {
  const { showAsVisited, checkInLabel, closingLabel } = getVisitState(item);
  const visitorName =
    `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim();

  return (
    <section
      className="appt-history-drawer__current"
      aria-label={strings.currentStatus}
    >
      <div className="appt-history-drawer__current-bar">
        <span className="appt-history-drawer__current-tag">
          {strings.currentStatus}
        </span>
        <ViewAction onView={onView} itemId={item._id} />
      </div>

      <div className="appt-history-drawer__current-top">
        <div className="appt-history-drawer__current-main">
          <p className="appt-history-drawer__current-date">
            {formatAppointmentCellValue(formatSiteVisit(item))}
          </p>
          <p className="appt-history-drawer__current-created">
            {strings.createdDate}:{" "}
            {item.createdDate
              ? moment(item.createdDate).format("DD-MM-yyyy hh:mm A")
              : "-"}
          </p>
        </div>
        <div className="appt-history-drawer__status-row">
          <StatusBadge
            label={closingLabel}
            badgeClass={getClosingStatusBadgeClass(closingLabel)}
          />
          <CheckInStatusBadge
            showAsVisited={showAsVisited}
            label={checkInLabel}
          />
        </div>
      </div>

      <dl className="appt-history-drawer__meta appt-history-drawer__meta--current">
        <div className="appt-history-drawer__meta-item">
          <dt>{strings.customerName}</dt>
          <dd>{formatAppointmentCellValue(visitorName)}</dd>
        </div>
        <div className="appt-history-drawer__meta-item">
          <dt>{strings.source}</dt>
          <dd>{formatAppointmentCellValue(item?.create_by)}</dd>
        </div>
        <div className="appt-history-drawer__meta-item">
          <dt>{strings.visitingScore}</dt>
          <dd className="appt-history-drawer__meta-score">
            {formatAppointmentCellValue(item?.lead_score)}
          </dd>
        </div>
      </dl>
    </section>
  );
};

const LeadStatusHistorySection = ({
  loading,
  error,
  history = [],
  latestLabel = "",
}) => {
  if (loading) {
    return (
      <section className="appt-history-drawer__status-section">
        <h3 className="appt-history-drawer__section-label">Status history</h3>
        <p className="appt-history-drawer__status-empty">Loading history…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="appt-history-drawer__status-section">
        <h3 className="appt-history-drawer__section-label">Status history</h3>
        <p className="appt-history-drawer__status-empty">{error}</p>
      </section>
    );
  }

  if (!history.length) {
    return (
      <section className="appt-history-drawer__status-section">
        <h3 className="appt-history-drawer__section-label">Status history</h3>
        <p className="appt-history-drawer__status-empty">
          No status history found
          {latestLabel ? ` · Current: ${latestLabel}` : ""}
        </p>
      </section>
    );
  }

  return (
    <section
      className="appt-history-drawer__status-section"
      aria-label="Status history"
    >
      <div className="appt-history-drawer__section-head">
        <h3 className="appt-history-drawer__section-label">
          Status history
          <span className="appt-history-drawer__section-count">
            {history.length}
          </span>
        </h3>
        <div className="appt-history-drawer__cols" aria-hidden="true">
          <span>Status</span>
          <span>Remark</span>
        </div>
      </div>

      <ol className="appt-history-drawer__status-list">
        {history.map((entry, index) => {
          const isLatest = index === 0;
          const remark = String(entry?.remark || "").trim();
          const hasRemark = Boolean(remark && remark !== "N/A");

          return (
            <li
              key={`appt-status-history-${index}-${entry.date || entry.label}`}
              className={`appt-history-drawer__history-card${
                isLatest ? " appt-history-drawer__history-card--latest" : ""
              }`}
            >
              <div className="appt-history-drawer__history-meta">
                <time
                  className="appt-history-drawer__history-date"
                  dateTime={entry.date || undefined}
                >
                  {formatHistoryDateTime(entry.date)}
                </time>
                {isLatest ? (
                  <span className="appt-history-drawer__history-latest">
                    Latest
                  </span>
                ) : null}
              </div>

              <div className="appt-history-drawer__history-main">
                <div className="appt-history-drawer__history-status">
                  <StatusBadge
                    label={entry.label || "N/A"}
                    badgeClass={getClosingStatusBadgeClass(entry.label)}
                  />
                </div>
                <p
                  className={`appt-history-drawer__history-remark${
                    hasRemark
                      ? ""
                      : " appt-history-drawer__history-remark--empty"
                  }`}
                >
                  {hasRemark ? renderHistoryRemarkText(remark) : "—"}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const AppointmentGroupHistoryDrawer = ({
  show = false,
  primary = null,
  onClose,
  onView,
}) => {
  const [statusHistory, setStatusHistory] = useState([]);
  const [latestLeadStatus, setLatestLeadStatus] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    if (!show) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, onClose]);

  useEffect(() => {
    if (!show || !primary?.lead_id) {
      setStatusHistory([]);
      setLatestLeadStatus("");
      setHistoryError("");
      setHistoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError("");

    appointmentService
      .getAppointmentLeadStatusHistory({
        lead_id: primary.lead_id,
        property_id: primary.property_id || "",
        suppressGlobalLoader: true,
      })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data || res?.data || {};
        const list = Array.isArray(payload.status_history)
          ? payload.status_history
          : [];
        setStatusHistory(list);
        setLatestLeadStatus(String(payload.latest_lead_status || "").trim());
        if (res?.data?.status && Number(res.data.status) >= 400) {
          setHistoryError(
            res?.data?.message || "Unable to load status history",
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatusHistory([]);
        setHistoryError("Unable to load status history");
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, primary?.lead_id, primary?.property_id, primary?._id]);

  if (!show || !primary) return null;

  const propertyName = formatAppointmentCellValue(primary?.property_title);
  const customerName =
    `${primary?.customer_first_name || ""} ${primary?.customer_last_name || ""}`.trim();

  return (
    <>
      <button
        type="button"
        className="appt-history-drawer__backdrop"
        aria-label="Close history modal"
        onClick={onClose}
      />
      <div
        className="appt-history-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appt-history-drawer-title"
      >
        <div className="appt-history-drawer__header">
          <div className="appt-history-drawer__header-main">
            <p className="appt-history-drawer__eyebrow">
              {strings.appointmentManagement}
            </p>
            <h2
              id="appt-history-drawer-title"
              className="appt-history-drawer__title"
            >
              {strings.viewHistory}
            </h2>
            <p className="appt-history-drawer__context">
              <span className="appt-history-drawer__context-item appt-history-drawer__context-item--strong">
                {propertyName}
              </span>
              {customerName ? (
                <>
                  <span
                    className="appt-history-drawer__context-sep"
                    aria-hidden="true"
                  >
                    ·
                  </span>
                  <span className="appt-history-drawer__context-item">
                    {formatAppointmentCellValue(customerName)}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            className="appt-history-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>

        <div className="appt-history-drawer__body">
          <CurrentVisitSummary item={primary} onView={onView} />

          <LeadStatusHistorySection
            loading={historyLoading}
            error={historyError}
            history={statusHistory}
            latestLabel={latestLeadStatus}
          />
        </div>
      </div>
    </>
  );
};

export default AppointmentGroupHistoryDrawer;
